import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Col, Empty, Row, Table, Typography } from 'antd'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { getRevenueMetrics, MetricsPeriod, MrrSummary } from '../../../Actions/operationsMetricsApi'
import StatCard from './StatCard'
import { formatCurrency, formatNumber, formatRate } from './formatters'

const PIE_COLORS = ['#1677ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1', '#13c2c2', '#fa541c', '#a0d911']

const mrrColumns = [
  {
    title: '통화',
    dataIndex: 'currencyCode',
    key: 'currencyCode',
    render: (currencyCode: string | null) => currencyCode || 'KRW',
  },
  {
    title: '결제방식',
    dataIndex: 'manualPayment',
    key: 'manualPayment',
    render: (manual: boolean) => (manual ? '수동(계약)' : '자동(PG)'),
  },
  {
    title: 'MRR',
    dataIndex: 'mrr',
    key: 'mrr',
    align: 'right' as const,
    render: (value: number, row: MrrSummary) => formatCurrency(row.currencyCode, value),
  },
  {
    title: 'ARR',
    dataIndex: 'arr',
    key: 'arr',
    align: 'right' as const,
    render: (value: number, row: MrrSummary) => formatCurrency(row.currencyCode, value),
  },
]

interface Props {
  period: MetricsPeriod
  prevPeriod: MetricsPeriod
}

/** 💰 매출 — 유료 워크스페이스 KPI(전기간 대비), 플랜 분포 도넛, 통화·결제방식별 MRR/ARR */
export default function RevenueSection({ period, prevPeriod }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['operationsMetrics', 'revenue', period],
    queryFn: () => getRevenueMetrics(period),
  })
  const { data: prev } = useQuery({
    queryKey: ['operationsMetrics', 'revenue', prevPeriod],
    queryFn: () => getRevenueMetrics(prevPeriod),
  })

  // 통화 미저장(null) 행과 'KRW' 행이 따로 그룹핑돼 같은 KRW가 여러 줄로 보이는 걸
  // 정규화 통화+결제방식 기준으로 합산해 한 줄로 합친다 (전부 KRW 전제)
  const mrrRows = useMemo(() => {
    const map = (data?.mrr ?? []).reduce((acc, row) => {
      const currencyCode = row.currencyCode || 'KRW'
      const key = `${currencyCode}:${row.manualPayment}`
      const cur = acc.get(key) ?? { currencyCode, manualPayment: row.manualPayment, mrr: 0, arr: 0 }
      acc.set(key, { ...cur, mrr: cur.mrr + row.mrr, arr: cur.arr + row.arr })
      return acc
    }, new Map<string, MrrSummary>())
    return Array.from(map.values())
  }, [data])

  if (error) return <Alert type="error" message="매출 지표를 불러오지 못했습니다." description={(error as Error).message} />

  const planData = (data?.planDistribution ?? []).filter((p) => p.workspaceCount > 0)

  return (
    <Card title="💰 매출 / 유료 전환" size="small">
      {/* 오늘 시점 스냅샷 — 선택한 기간과 무관하게 '지금' 상태. 아래 기간 지표와 시계가 다르므로 분리해 표기 */}
      <Typography.Text strong style={{ fontSize: 13 }}>
        📌 현재 기준 <Typography.Text type="secondary">(오늘 시점 · 선택 기간 무관)</Typography.Text>
      </Typography.Text>
      <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
        <Col xs={12} md={6}>
          <StatCard
            title="유료 워크스페이스"
            value={data ? formatNumber(data.activePaidWorkspaces) : '—'}
            caption="오늘 시점 활성 유료 수"
            tooltip="선택한 기간과 무관하게, 오늘 이 순간 유료 구독이 활성인 워크스페이스 수입니다. 아래 '선택 기간 변화' 지표와는 기준 시점이 달라 직접 비교되지 않습니다."
            loading={isLoading}
          />
        </Col>
      </Row>

      {/* 선택 기간 동안의 유입/유출 */}
      <Typography.Text strong style={{ fontSize: 13, display: 'block', marginTop: 16 }}>
        📆 선택 기간 변화 <Typography.Text type="secondary">(기간 내 발생 · 전기간 대비 증감)</Typography.Text>
      </Typography.Text>
      <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
        <Col xs={12} md={6}>
          <StatCard
            title="신규 유료 전환"
            value={data ? formatNumber(data.newPaidWorkspaces) : '—'}
            rawValue={data?.newPaidWorkspaces}
            previous={prev?.newPaidWorkspaces}
            caption="기간 내 생애 첫 전환"
            tooltip="선택 기간 동안 처음으로 유료 구독을 시작한 워크스페이스 수입니다. 이전에 한 번이라도 유료였던 곳은 제외됩니다."
            valueColor="#3f8600"
            loading={isLoading}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="이탈 건수"
            value={data ? formatNumber(data.churnedPaidWorkspaces) : '—'}
            rawValue={data?.churnedPaidWorkspaces}
            previous={prev?.churnedPaidWorkspaces}
            caption="기간 내 빠져나간 모든 곳"
            tooltip="선택 기간 안에 유료를 해지하고 지금도 유료가 아닌 워크스페이스 수입니다. 기간 중 새로 들어왔다가 해지한 단기 고객까지 전부 셉니다. → '몇 곳이 나갔나'. 오른쪽 '기존 고객 이탈률'과는 세는 대상이 다릅니다."
            valueColor="#cf1322"
            loading={isLoading}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="기존 고객 이탈률"
            value={data?.churnRate != null ? formatRate(data.churnRate) : '—'}
            caption={
              data?.churnRate != null ? `기간 시작 단골 ${formatNumber(data.activePaidAtStart)}곳 중` : '기준 단골 없음'
            }
            tooltip="기간 시작 시점에 이미 유료였던 '단골' 워크스페이스 중 기간 끝까지 유지하지 못한 비율입니다. → '원래 단골이 얼마나 빠졌나'. 왼쪽 '이탈 건수'와 달리, 기간 중간에 새로 들어온 곳은 여기 분모에 없습니다. (유료→유료 플랜 변경은 이탈로 보지 않음 · 기준 단골이 0곳이면 '—')"
            valueColor="#cf1322"
            loading={isLoading}
          />
        </Col>
      </Row>
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} md={10}>
          <Typography.Text strong>플랜별 유료 워크스페이스 분포</Typography.Text>
          {planData.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="데이터 없음" />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={planData}
                  dataKey="workspaceCount"
                  nameKey="planName"
                  innerRadius={50}
                  outerRadius={80}
                >
                  {planData.map((entry, index) => (
                    <Cell key={entry.planName} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Legend
                  formatter={(value, entry) =>
                    `${value} (${formatNumber(Number((entry?.payload as { workspaceCount?: number })?.workspaceCount ?? 0))})`
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Col>
        <Col xs={24} md={14}>
          <Typography.Text strong>MRR / ARR (현재 활성 구독 기준 · 통화별 분리)</Typography.Text>
          <Table
            rowKey={(row: MrrSummary) => `${row.currencyCode}:${row.manualPayment}`}
            columns={mrrColumns}
            dataSource={mrrRows}
            size="small"
            pagination={false}
            loading={isLoading}
            style={{ marginTop: 8 }}
          />
        </Col>
      </Row>
    </Card>
  )
}
